import type { PvdConfigurationOnAzure, UpdatePvdConfigRequest } from '@/../generated-client/models';
import { usePvdConfigFields, type PvdConfigFieldValues } from './usePvdConfigFields';

export function usePvdConfigEdit(config: PvdConfigurationOnAzure) {
  const initialValues: PvdConfigFieldValues = {
    specName: config.spec_name,
    voltage: config.voltage_allowed_settings,
    current: config.current_allowed_settings,
    adaptiveVoltageAllowed: config.adaptive_voltage_allowed,
    adaptiveCurrentAllowed: config.adaptive_current_allowed,

    autoAdaptIsLocked: config.adaptive_shared_auto_adapt_is_locked,
    autoAdaptLockedToValue: config.adaptive_shared_auto_adapt_locked_to_value,

    voltageBubbleSizeIsLocked: config.adaptive_voltage_next_alert_size_is_locked,
    voltageBubbleSizeLockedToValue: config.adaptive_voltage_next_alert_size_locked_to_value,
    // API stores 0-based firmware indices; slider displays 1-based UI labels. +1 converts.
    // A stored min=0 becomes UI label 1, safely within <Slider min={1}> — no clamping occurs.
    voltageMinSetting: config.adaptive_voltage_min_setting + 1,
    voltageMaxSetting: config.adaptive_voltage_max_setting + 1,

    currentBubbleSizeIsLocked: config.adaptive_current_next_alert_size_is_locked,
    currentBubbleSizeLockedToValue: config.adaptive_current_next_alert_size_locked_to_value,
    currentMinSetting: config.adaptive_current_min_setting + 1,
    currentMaxSetting: config.adaptive_current_max_setting + 1,
  };

  const fields = usePvdConfigFields(initialValues);

  const submitConfig = (shouldForceApply: boolean): UpdatePvdConfigRequest => ({
    org_code: config.org_code,
    config_id: config.id,
    spec_name: fields.specName,
    is_compass_core: config.is_compass_core,

    voltage_allowed_settings: fields.voltage,
    adaptive_voltage_allowed: fields.adaptiveVoltageAllowed,
    current_allowed_settings: fields.current,
    adaptive_current_allowed: fields.adaptiveCurrentAllowed,

    adaptive_shared_auto_adapt_is_locked: fields.autoAdaptIsLocked,
    adaptive_shared_auto_adapt_locked_to_value: fields.autoAdaptLockedToValue,

    adaptive_voltage_next_alert_size_is_locked: fields.voltageBubbleSizeIsLocked,
    adaptive_voltage_next_alert_size_locked_to_value: fields.voltageBubbleSizeLockedToValue,
    // 1-based UI label → 0-based firmware index. API validates max ≤ 21 (voltage) / ≤ 10 (current).
    adaptive_voltage_min_setting: fields.voltageMinSetting - 1,
    adaptive_voltage_max_setting: fields.voltageMaxSetting - 1,

    adaptive_current_next_alert_size_is_locked: fields.currentBubbleSizeIsLocked,
    adaptive_current_next_alert_size_locked_to_value: fields.currentBubbleSizeLockedToValue,
    adaptive_current_min_setting: fields.currentMinSetting - 1,
    adaptive_current_max_setting: fields.currentMaxSetting - 1,

    should_force_apply_this_update: shouldForceApply,
  });

  return { ...fields, submitConfig };
}
